/**
 * Чтение ZIP-архива по центральному каталогу — файл за файлом, без загрузки
 * архива в память.
 *
 * Зачем не `fflate.unzip` / потоковый `Unzip`: архив РД на объект — сотни
 * мегабайт и больше; `unzip` держит в памяти весь архив и все распакованные
 * файлы сразу, а потоковый разбор не знает размеров записей с дескриптором
 * данных (так пишут архиваторы Windows) и ищет конец записи по сигнатуре
 * следующего заголовка — в сжатых данных она изредка встречается случайно, и
 * файл рвётся («unexpected EOF» на 46 МБ PDF в реальном архиве).
 *
 * Центральный каталог в конце архива даёт точные смещения и размеры: каждая
 * запись читается срезом `Blob.slice` и распаковывается порциями; распакованное
 * каждые ~16 МБ сбрасывается в промежуточный `Blob`, так что пик памяти — порция,
 * а не весь файл. Целостность — по CRC-32 из центрального каталога: битый файл
 * не уходит на сервер молча. Поддержаны ZIP64 (архивы больше 4 ГБ), имена в
 * UTF-8 и в DOS-кодировке cp866 (архивы «Проводника» без флага UTF-8).
 * Зашифрованные записи и экзотические методы сжатия не читаются — вызывающий
 * показывает предупреждение.
 */
import { Inflate } from 'fflate';

export interface ZipEntry {
  /** Путь внутри архива (`101/Р-ЗЛ-….pdf`). */
  path: string;
  /** Размер распакованного файла, байт. */
  size: number;
  compressedSize: number;
  /** 0 — без сжатия, 8 — deflate. */
  method: number;
  encrypted: boolean;
  /** Смещение локального заголовка записи. */
  offset: number;
  /** CRC-32 распакованного файла (из центрального каталога) — сверяется при чтении. */
  crc32: number;
}

export interface ReadZipEntryOptions {
  /** Прервать чтение (между порциями) — бросает `AbortError`. */
  signal?: AbortSignal;
}

const SIG_EOCD = 0x06054b50;
const SIG_EOCD64_LOCATOR = 0x07064b50;
const SIG_EOCD64 = 0x06064b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;
const MAX16 = 0xffff;
const MAX32 = 0xffffffff;
/** Порция чтения записи из архива. */
const READ_CHUNK = 4 << 20;
/** Порция сжатых данных на один шаг распаковки — ограничивает размер выходного куска. */
const INFLATE_CHUNK = 512 << 10;
/** Распакованное сбрасывается в промежуточный Blob каждые ~16 МБ. */
const FLUSH_BYTES = 16 << 20;
/** Атрибут MS-DOS «каталог» во внешних атрибутах записи. */
const DOS_DIRECTORY = 0x10;
/** Хост «Unix» в поле «version made by» и тип файла «каталог» в его режиме. */
const HOST_UNIX = 3;
const UNIX_TYPE_MASK = 0xf000;
const UNIX_DIRECTORY = 0x4000;

const NOT_ZIP = 'файл не похож на ZIP-архив (нет оглавления)';
const BAD_CD = 'оглавление ZIP повреждено — архив недокачан или испорчен';

async function read(file: Blob, start: number, end: number): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(start, end).arrayBuffer());
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException('чтение архива прервано', 'AbortError');
}

function isAbortError(e: unknown): boolean {
  return (e as { name?: unknown } | null)?.name === 'AbortError';
}

// ── CRC-32 (полином 0xEDB88320, как в ZIP) ──

let crcTable: Int32Array | null = null;

function table(): Int32Array {
  if (crcTable) return crcTable;
  const t = new Int32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c;
  }
  crcTable = t;
  return t;
}

/** CRC-32 порции данных; `crc` — значение по предыдущим порциям (0 — начало). */
export function crc32(data: Uint8Array, crc = 0): number {
  const t = table();
  let c = ~crc;
  for (let i = 0; i < data.length; i++) c = t[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return ~c >>> 0;
}

/** Имя записи: флаг UTF-8 — UTF-8; без флага — UTF-8, если валиден, иначе cp866. */
function decodeName(bytes: Uint8Array, utf8: boolean): string {
  if (utf8) return new TextDecoder('utf-8').decode(bytes);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('ibm866').decode(bytes);
  }
}

/**
 * Конец центрального каталога в последних байтах файла: сигнатура, после которой
 * ровно 22 байта и комментарий заявленной длины до конца файла. Сигнатура может
 * случайно встретиться в комментарии — такой кандидат пропускаем и ищем дальше.
 * Если точного нет (после архива дописаны байты), берём ближайший к концу —
 * его поля всё равно проверяются при чтении оглавления.
 */
function findEocd(tail: Uint8Array): number {
  const tv = view(tail);
  let loose = -1;
  for (let i = tail.length - 22; i >= 0; i--) {
    if (tv.getUint32(i, true) !== SIG_EOCD) continue;
    if (i + 22 + tv.getUint16(i + 20, true) === tail.length) return i;
    if (loose < 0) loose = i;
  }
  return loose;
}

/** Оглавление архива: записи-файлы (каталоги пропущены), в порядке архива. */
export async function listZip(file: Blob): Promise<ZipEntry[]> {
  if (file.size < 22) throw new Error(NOT_ZIP);
  // Конец центрального каталога — в последних 22 байтах + комментарий (до 64 КБ).
  const tailStart = Math.max(0, file.size - (22 + MAX16));
  const tail = await read(file, tailStart, file.size);
  const eocd = findEocd(tail);
  if (eocd < 0) throw new Error(NOT_ZIP);
  const tv = view(tail);
  let count = tv.getUint16(eocd + 10, true);
  let cdSize = tv.getUint32(eocd + 12, true);
  let cdOffset = tv.getUint32(eocd + 16, true);
  if (count === MAX16 || cdSize === MAX32 || cdOffset === MAX32) {
    // ZIP64: локатор — сразу перед концом каталога.
    const locAt = tailStart + eocd - 20;
    const loc = locAt >= 0 ? view(await read(file, locAt, locAt + 20)) : null;
    if (loc && loc.getUint32(0, true) === SIG_EOCD64_LOCATOR) {
      const at = Number(loc.getBigUint64(8, true));
      if (at + 56 > file.size) throw new Error('повреждённое оглавление ZIP64');
      const e64 = view(await read(file, at, at + 56));
      if (e64.getUint32(0, true) !== SIG_EOCD64) throw new Error('повреждённое оглавление ZIP64');
      count = Number(e64.getBigUint64(32, true));
      cdSize = Number(e64.getBigUint64(40, true));
      cdOffset = Number(e64.getBigUint64(48, true));
    } else if (cdSize === MAX32 || cdOffset === MAX32) {
      throw new Error('повреждённое оглавление ZIP64');
    }
    // Иначе — обычный архив ровно на 65 535 записей.
  }
  if (cdOffset + cdSize > file.size) throw new Error(BAD_CD);
  const cd = await read(file, cdOffset, cdOffset + cdSize);
  const cv = view(cd);
  const out: ZipEntry[] = [];
  let p = 0;
  for (let n = 0; n < count; n++) {
    // Каталог короче заявленного числа записей — архив недокачан или испорчен:
    // молча вернуть часть файлов нельзя.
    if (p + 46 > cd.length || cv.getUint32(p, true) !== SIG_CENTRAL) throw new Error(BAD_CD);
    const host = cv.getUint8(p + 5);
    const flags = cv.getUint16(p + 8, true);
    const method = cv.getUint16(p + 10, true);
    const crc = cv.getUint32(p + 16, true);
    let compressedSize = cv.getUint32(p + 20, true);
    let size = cv.getUint32(p + 24, true);
    const nameLen = cv.getUint16(p + 28, true);
    const extraLen = cv.getUint16(p + 30, true);
    const commentLen = cv.getUint16(p + 32, true);
    const attrs = cv.getUint32(p + 38, true);
    let offset = cv.getUint32(p + 42, true);
    const nameEnd = p + 46 + nameLen;
    const extraEnd = nameEnd + extraLen;
    const next = extraEnd + commentLen;
    if (next > cd.length) throw new Error(BAD_CD);
    const name = decodeName(cd.subarray(p + 46, nameEnd), (flags & 0x800) !== 0);
    // ZIP64: настоящие размеры и смещение — в дополнительном поле 0x0001.
    for (let e = nameEnd; e + 4 <= extraEnd;) {
      const id = cv.getUint16(e, true);
      const len = cv.getUint16(e + 2, true);
      if (id === 0x0001) {
        const end = Math.min(e + 4 + len, extraEnd);
        let q = e + 4;
        if (size === MAX32 && q + 8 <= end) {
          size = Number(cv.getBigUint64(q, true));
          q += 8;
        }
        if (compressedSize === MAX32 && q + 8 <= end) {
          compressedSize = Number(cv.getBigUint64(q, true));
          q += 8;
        }
        if (offset === MAX32 && q + 8 <= end) offset = Number(cv.getBigUint64(q, true));
      }
      e += 4 + len;
    }
    p = next;
    const path = name.replace(/\\/g, '/');
    // Каталог: имя на «/» или атрибут «каталог» (MS-DOS; у Unix-архивов — тип в
    // режиме файла) — так пишут некоторые архиваторы без завершающей «/».
    const dirAttr =
      (attrs & DOS_DIRECTORY) !== 0 ||
      (host === HOST_UNIX && ((attrs >>> 16) & UNIX_TYPE_MASK) === UNIX_DIRECTORY);
    if (!path || path.endsWith('/') || (dirAttr && size === 0)) continue;
    out.push({
      path,
      size,
      compressedSize,
      method,
      encrypted: (flags & 1) !== 0,
      offset,
      crc32: crc,
    });
  }
  return out;
}

const corrupted = (why: string) => new Error(`файл в архиве повреждён (${why})`);

/**
 * Содержимое записи с проверкой CRC-32. Без сжатия — срез исходного файла (без
 * копии в памяти; CRC считается проходом по срезам). Deflate — порциями, с
 * промежуточными Blob каждые ~16 МБ.
 */
export async function readZipEntry(
  file: Blob,
  entry: ZipEntry,
  options: ReadZipEntryOptions = {},
): Promise<Blob> {
  const { signal } = options;
  if (entry.encrypted) throw new Error('файл в архиве зашифрован');
  if (entry.method !== 0 && entry.method !== 8) {
    throw new Error(`метод сжатия ${entry.method} не поддерживается`);
  }
  throwIfAborted(signal);
  if (entry.offset + 30 > file.size) throw corrupted('запись за пределами архива');
  const lh = view(await read(file, entry.offset, entry.offset + 30));
  if (lh.getUint32(0, true) !== SIG_LOCAL) throw corrupted('нет заголовка записи');
  const start = entry.offset + 30 + lh.getUint16(26, true) + lh.getUint16(28, true);
  const end = start + entry.compressedSize;
  if (end > file.size) throw corrupted('архив обрезан');

  let crc = 0;
  if (entry.method === 0) {
    if (entry.compressedSize !== entry.size) throw corrupted('размер');
    for (let pos = start; pos < end; pos += READ_CHUNK) {
      throwIfAborted(signal);
      crc = crc32(await read(file, pos, Math.min(end, pos + READ_CHUNK)), crc);
    }
    if (crc !== entry.crc32) throw corrupted('CRC');
    return file.slice(start, end);
  }

  if (entry.compressedSize === 0) {
    // Пустой поток deflate невалиден; пустой файл так иногда пишут.
    if (entry.size !== 0 || entry.crc32 !== 0) throw corrupted('нет данных');
    return new Blob([]);
  }
  const blobs: Blob[] = [];
  let parts: Uint8Array[] = [];
  let pending = 0;
  let total = 0;
  const inflate = new Inflate();
  inflate.ondata = (chunk) => {
    if (chunk.length === 0) return;
    crc = crc32(chunk, crc);
    total += chunk.length;
    parts.push(chunk);
    pending += chunk.length;
    if (pending >= FLUSH_BYTES) {
      blobs.push(new Blob(parts as BlobPart[]));
      parts = [];
      pending = 0;
    }
  };
  try {
    for (let pos = start; pos < end; pos += READ_CHUNK) {
      throwIfAborted(signal);
      const stop = Math.min(end, pos + READ_CHUNK);
      const bytes = await read(file, pos, stop);
      for (let i = 0; i < bytes.length; i += INFLATE_CHUNK) {
        const last = stop >= end && i + INFLATE_CHUNK >= bytes.length;
        inflate.push(bytes.subarray(i, Math.min(bytes.length, i + INFLATE_CHUNK)), last);
      }
    }
  } catch (e) {
    if (isAbortError(e)) throw e;
    throw corrupted(`распаковка: ${(e as Error).message}`);
  }
  if (total !== entry.size) throw corrupted('размер');
  if (crc !== entry.crc32) throw corrupted('CRC');
  if (parts.length > 0) blobs.push(new Blob(parts as BlobPart[]));
  return new Blob(blobs);
}
